import React from 'react'
import { useNavigate } from 'react-router-dom'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import BirthDateSelector from './components/BirthDateSelector'
import CountrySelector from '@/components/CountrySelector'
import TipCard from '@/components/TipCard'
import IdentificationTypeSelector from './components/IdentificationTypeSelector'
import DocumentUpload from './components/DocumentUpload'
import SuccessStep from './components/SuccessStep'
import { useKycForm } from './hooks/useKycForm'
import { useKycFileUpload } from './hooks/useKycFileUpload'
import { useKycSubmit } from './hooks/useKycSubmit'
import { useKycData } from './hooks/useKycData'
import { KycStep } from './hooks'
import { useTranslation } from 'react-i18next'
import LogoTitle from '@/components/logoTitle'

export default React.memo(function KycCertification() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // 使用新的hooks
  const {
    formData,
    setFormData,
    handleInputChange,
    isBaseInfoFormValid,
    isIdentificationFormValid,
    validateBaseInfoForm,
    validateIdentificationForm
  } = useKycForm()

  const {
    handleImageRemove,
    handleImageUpload,
    handleImageUploadError,
    passportFile,
    setPassportFile,
    idCardFrontFile,
    setIdCardFrontFile,
    idCardBackFile,
    setIdCardBackFile,
    idCardHandHoldFile,
    setIdCardHandHoldFile
  } = useKycFileUpload(formData, setFormData)

  const {
    currentStep,
    setCurrentStep,
    loading,
    handleNext,
    handleSubmit
  } = useKycSubmit(formData, validateBaseInfoForm, validateIdentificationForm)

  const { countryLists, identificationTypes } = useKycData()

  // 渲染基本信息步骤
  const renderBaseInfoStep = () => (
    <div>
      <TipCard message={t('您提交的个人信息将通过加密通道传输和存储我们会严格保护您的数据安全')} />

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('请输入您的国家或地区')}</label>
          <CountrySelector
            value={formData.country}
            onChange={(value: string) => {
              setFormData(prev => ({ ...prev, country: value }))
            }}
            placeholder={t('请选择国家')}
            options={countryLists}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('请输入您的姓氏')}</label>
          <CustomTextInput
            value={formData.last_name}
            onChangeText={(value: string) => handleInputChange('last_name', value)}
            placeholder={t('请输入您的姓氏')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('请输入您的名字')}</label>
          <CustomTextInput
            value={formData.first_name}
            onChangeText={(value: string) => handleInputChange('first_name', value)}
            placeholder={t('请输入您的名')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('请选择您的出生日期')}</label>
          <BirthDateSelector
            year={formData.birthYear}
            month={formData.birthMonth}
            day={formData.birthDay}
            onYearChange={(value: string) => handleInputChange('birthYear', value)}
            onMonthChange={(value: string) => handleInputChange('birthMonth', value)}
            onDayChange={(value: string) => handleInputChange('birthDay', value)}
          />
        </div>
      </div>
      <div className="my-8">
        <CustomButton
          onClick={handleNext}
          disabled={!isBaseInfoFormValid}

        >
          {t('下一步')}
        </CustomButton>
      </div>
    </div>
  )

  // 渲染证件信息步骤
  const renderIdentificationStep = () => (
    <div>
      <TipCard message={t('您提交的个人信息将通过加密通道传输和存储我们会严格保护您的数据安全')} />

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('请选择您的证件类型')}</label>
          <IdentificationTypeSelector
            value={formData.identity_type}
            onChange={(value: string) => handleInputChange('identity_type', value)}
            placeholder={t('请选择证件类型')}
            options={identificationTypes}
          />
        </div>

        {formData.identity_type === 'passport' ? (
          <DocumentUpload
            label={t('请选择上传您的护照照片')}
            value={passportFile}
            imageUrl={formData.passport}
            fieldKey="passport"
            onFileChange={(file: File | null) => setPassportFile(file)}
            onUploadSuccess={(fileUrl: string, file: File) => handleImageUpload('passport', fileUrl, file)}
            onUploadError={handleImageUploadError}
            onRemove={() => handleImageRemove('passport')}
          />
        ) : formData.identity_type === 'id_card' ? (
          <>
            <DocumentUpload
              label={t('请选择上传您的身份证人像面')}
              value={idCardFrontFile}
              imageUrl={formData.id_card.front}
              fieldKey="id_card.front"
              onFileChange={(file: File | null) => setIdCardFrontFile(file)}
              onUploadSuccess={(fileUrl: string, file: File) => handleImageUpload('id_card.front', fileUrl, file)}
              onUploadError={handleImageUploadError}
              onRemove={() => handleImageRemove('id_card.front')}
            />

            <DocumentUpload
              label={t('请选择上传您的身份证国徽面')}
              value={idCardBackFile}
              imageUrl={formData.id_card.back}
              fieldKey="id_card.back"
              onFileChange={(file: File | null) => setIdCardBackFile(file)}
              onUploadSuccess={(fileUrl: string, file: File) => handleImageUpload('id_card.back', fileUrl, file)}
              onUploadError={handleImageUploadError}
              onRemove={() => handleImageRemove('id_card.back')}
            />

            <DocumentUpload
              label={t('请选择上传您的手持身份证照片')}
              value={idCardHandHoldFile}
              imageUrl={formData.id_card.hand_hold}
              fieldKey="id_card.hand_hold"
              onFileChange={(file: File | null) => setIdCardHandHoldFile(file)}
              onUploadSuccess={(fileUrl: string, file: File) => handleImageUpload('id_card.hand_hold', fileUrl, file)}
              onUploadError={handleImageUploadError}
              onRemove={() => handleImageRemove('id_card.hand_hold')}
            />
          </>
        ) : (
          <div></div>
        )}
      </div>

      <div className="my-8">
        <CustomButton
          onClick={handleSubmit}
          loading={loading}
          disabled={!isIdentificationFormValid}
        >
          {t('提交审核')}
        </CustomButton>
      </div>
    </div>
  )

  // 渲染成功步骤
  const renderSuccessStep = () => (
    <SuccessStep
      title={t('提交成功')}
      subtitle={t('我们将在24小时内完成审核')}
      buttonText={t('返回首页')}
      onButtonClick={() => navigate('/')}
    />
  )

  const getCurrentStepContent = () => {
    switch (currentStep) {
      case KycStep.BaseInfo:
        return renderBaseInfoStep()
      case KycStep.Identification:
        return renderIdentificationStep()
      case KycStep.SubmitSuccess:
        return renderSuccessStep()
      default:
        return renderBaseInfoStep()
    }
  }

  return (
    <PageLayout>
      <Header
        leftChildren={<Back onClick={() => {
          if (currentStep === KycStep.BaseInfo) {
            navigate(-1)
          } else {
            // 使用hooks中的setCurrentStep
            setCurrentStep(KycStep.BaseInfo)
          }
        }} />}
        centerChildren={<LogoTitle />}
      />
      <div className="bg-white h-full">
        {/* Content */}
        <div className="px-6 py-6">
          {getCurrentStepContent()}
        </div>
      </div>
    </PageLayout>
  )
})